import CirclebusinessMap from '@/common/components/business/ExpandStore/CircleBusinessMap';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import styles from '../index.module.less';
import { Cascader, Col, Form, Row } from 'antd';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isArray, isNotEmptyAny, refactorSelection } from '@lhb/func';
import { employeeListPermission } from '@/common/api/employee';
import { getStandardTaskSelection } from '@/common/api/expandStore/expansiontask';
import FormBusinessMode from '../../../components/FormBusinessMode';
import FormFranchisee from '@/common/components/business/ExpandStore/TaskCreateDrawer/components/FormFranchisee';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import FormFollowingStatus from '../../../components/FormFollowingStatus';
import { AssignType, BusinessMode, franchiseeShortTitleForMode, FranchiseeTypeNameJoint } from '@/common/components/business/ExpandStore/ts-config';
import { globalOpeningAreaAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';

interface EditMainProps {
  style?: any;
  className?: any;
  isBusiness?: boolean;
  form?: any;
  businessInfo?: any;
  franchiseeDetail?: any;
  isFromFranchisee?: boolean;
  franchiseeAlias?: string;
  taskAssignType?: AssignType; // 指派类型
  taskAssignRoleIds?: any; // 指派类型为指派主管时，可指派对象的角色
  expansionTaskAlias?: string; // 拓店任务别名
}

/**
 * 拓店任务：加盟+直营模板A-新增/编辑-主体
 * @Description
 */
const EditMain: FC<EditMainProps> = ({
  style,
  className,
  isBusiness,
  form,
  businessInfo,
  franchiseeDetail,
  isFromFranchisee,
  franchiseeAlias,
  taskAssignType,
  taskAssignRoleIds,
  expansionTaskAlias
}) => {


  const circlebusinessMap: any = useRef();
  const [employeeOptions, setEmployeeOptions] = useState<any>([]); // 指派人选项
  const [emergencyDegree, setEmergencyDegree] = useState<any>([]);
  const [uniqueName, setUniqueName] = useState<string>(`${franchiseeAlias}唯一标识`); // 加盟商唯一字段名称
  const [franchiseeInited, setFranchiseeInited] = useState<boolean>(false); // 默认加盟商信息时，是否已完成初始化赋值
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const openingAreaTaskAlias = useMemo(() => {
    return globalOpeningAreaAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  const isAssignTypeLeader = taskAssignType && [AssignType.Twice, AssignType.ThreeTimes].includes(taskAssignType); // 是否指派主管（二次、三次指派兼容为同样处理）

  const targetAddressInfo = Form.useWatch('targetAddressInfo', form);
  const businessName = Form.useWatch('businessName', form);
  const businessMode = Form.useWatch('businessMode', form);
  const followingStatus = Form.useWatch('followingStatus', form);
  const standardFranchiseeId = Form.useWatch('standardFranchiseeId', form);

  const uniqueDefaultNameForMode = (businessMode) => {
    const isJoint = businessMode === BusinessMode.Joint;
    return isJoint ? '联营商唯一标识' : `${franchiseeAlias}唯一标识`;
  };

  useEffect(() => {
    getEmployeeOptions();
    if (isBusiness) {
      const { id, areaName, provinceId, cityId, districtId, provinceName, cityName, districtName } = businessInfo;
      form.setFieldsValue({
        businessName: areaName,
        targetAddress: [provinceId, cityId, districtId],
        targetAddressInfo: [provinceName, cityName, districtName],
        circleBusinessId: id
      });

    }
  }, []);

  useEffect(() => {
    // const targetCity = targetAddressInfo && isArray(targetAddressInfo) ? `${targetAddressInfo.slice(0, 2).join('/')}_` : '';
    const targetCity = targetAddressInfo && isNotEmptyAny(targetAddressInfo[1]) ? `${targetAddressInfo[1]}_` : '';
    const targetBusinessName = businessName ? `${businessName}_` : '';

    const taskName = (targetCity || targetBusinessName) ? `${targetCity}${targetBusinessName}${expansionTaskAlias || '拓店任务'}` : '';
    form.setFieldsValue({
      name: taskName
    });
  }, [targetAddressInfo, businessName]);

  useEffect(() => {
    // 从加盟商列表创建任务，并且拿到加盟商详情后，作为默认值
    if (!isFromFranchisee || !isNotEmptyAny(franchiseeDetail)) return;
    const { uniqueName: uniName, franchiseeTypeName } = franchiseeDetail;

    const defaultMode = franchiseeTypeName === FranchiseeTypeNameJoint ? BusinessMode.Joint : BusinessMode.Franchisee; // 按照类型来默认业务模式
    // 初始化回显加盟商信息step1，先设置模式
    form.setFieldsValue({
      businessMode: defaultMode,
    });
    setUniqueName(uniName || uniqueDefaultNameForMode(defaultMode));
  }, [franchiseeDetail]);

  useEffect(() => {
    // 合作模式变动时，修改标识符默认名称
    setUniqueName(uniqueDefaultNameForMode(businessMode));

    // 来自加盟商页的默认模式，还未初始化，不清空
    if (isFromFranchisee && !franchiseeInited && businessMode) {

      // 初始化回显加盟商信息step2，模式确定后才能动态决定组件，此时设置加盟商信息
      const { id, name, uniqueId } = franchiseeDetail;
      form.setFieldsValue({
        standardFranchiseeId: id,
        franchiseeName: name,
        uniqueId,
      });

      setFranchiseeInited(true);
      return;
    }
    // 属于后续变动，清空之前的加盟商
    form.setFieldsValue({
      standardFranchiseeId: null,
      franchiseeName: null,
      uniqueId: null
    });
  }, [businessMode]);

  /**
   * @description 根据合作模式，返回字段标题
   * @return
   */
  const franchiseeTitle = useMemo(() => {
    const isJoint = businessMode === BusinessMode.Joint;
    return isJoint ? '联营商姓名' : `${franchiseeAlias}姓名`;
  }, [businessMode]);

  /**
   * @description 根据合作模式，返回字段简略标题
   * @return
   */
  const franchiseeShortTitle = useMemo(() => {
    return franchiseeShortTitleForMode(businessMode);
  }, [businessMode]);

  /**
   * @description 获取可指派的团队人员
   */
  const getEmployeeOptions = async () => {
    const params = isAssignTypeLeader && isArray(taskAssignRoleIds) ? { roleIds: taskAssignRoleIds } : {};
    const { objectList: data } = await employeeListPermission(params);
    const options = await getStandardTaskSelection();
    const arr = isArray(data) && data.length ? data.map((item) => ({
      ...item,
      name: `${item.name} ${item.mobile}`
    })) : [];
    const emergencyData = options?.emergencyDegree.map((item) => ({
      label: item.name,
      value: item.id
    })) || [];
    setEmergencyDegree(emergencyData);
    setEmployeeOptions(arr);
    if (isAssignTypeLeader && arr.length === 1) { // 指派主管且只有一个时默认指定
      form.setFieldValue('managerId', arr[0].id);
    }
  };

  const handleFranchiseeCreated = (val: any) => {

    form.setFieldValue('standardFranchiseeId', val);

    // 新增加盟商后获取加盟商详情
    getFranchiseeInfo(val);
  };

  const handleFranchisee = (val: any) => {
    form.setFieldValue('standardFranchiseeId', val);

    // 选择加盟商后获取加盟商详情
    getFranchiseeInfo(val);
  };

  /**
   * @description 加盟商详情
   * @param franId
   * @return
   */
  const getFranchiseeInfo = (franId: number) => {
    if (franId) {
      getFranchiseeDetail({ id: franId }).then((data) => {
        const { name, uniqueId, uniqueName: uniName } = data;
        form.setFieldsValue({
          franchiseeName: name,
          uniqueId: uniqueId,
        });
        setUniqueName(uniName || uniqueDefaultNameForMode(businessMode));
      });
    } else {
      form.setFieldsValue({
        uniqueId: undefined,
      });
    }
  };

  return (
    <>
      <V2Form style={{ ...style }} className={className} form={form}>
        <V2Title type='H2' divider text={`${expansionTaskAlias || '拓店任务'}信息`} className={'mb-16'} />
        <Row gutter={24}>
          <Col span={24} className='mb-16'>
            <CirclebusinessMap
              form ={form}
              ref={circlebusinessMap}
              businessInfo={businessInfo}
              isBusiness={isBusiness}
            />
          </Col>
          <Col span={12}>
            <V2FormInput
              required
              label={openingAreaTaskAlias}
              name='businessName'
              disabled={true}
              placeholder={`请在地图上选择${openingAreaTaskAlias}`}
            />
          </Col>
          <Col span={12}>
            <Form.Item name='targetAddressInfo' hidden noStyle/>
            <V2FormProvinceList
              required
              label='目标城市'
              name='targetAddress'
              disabled={true}
              config={{
                value: [1],
                changeOnSelect: true,
                allowClear: true,
                expandTrigger: 'hover',
                showCheckedStrategy: Cascader.SHOW_PARENT,
                onChange: (_, selectedOptions) => {
                  circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onChangeProvince(_, selectedOptions);
                },
                onDropdownVisibleChange: (value) => {
                  circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onDropdownVisibleChange(value);

                }
              }}
            />
          </Col>
          <Col span={12}>
            <V2FormInput
              required
              label={`${expansionTaskAlias || '任务'}名称`}
              name='name'
              maxLength={50}
            />
          </Col>
          <Col span={12}>
            <FormFollowingStatus
              required
              disabled={[4, 5].includes(followingStatus)} // 如果是已确定、已开业不能修改
              label='跟进状态'
              placeholder='请选择跟进状态'
              name='followingStatus'
              isEdit/>
          </Col>
          <Col span={12}>
            <FormBusinessMode
              label='业务模式'
              placeholder='请选择业务模式'
              name='businessMode'/>
          </Col>
          {/* 如果选择加盟模式，则出现加盟商 */}
          {[BusinessMode.Franchisee, BusinessMode.Joint].includes(businessMode)
            ? <>
              <Col span={12}>
                <Form.Item name='standardFranchiseeId' hidden noStyle/>
                {/* option不同，选择组件区分使用 */}
                {businessMode === BusinessMode.Franchisee && <FormFranchisee
                  label={franchiseeTitle}
                  name='franchiseeName'
                  placeholder={`请输入${franchiseeTitle}`}
                  createBtnTitle={`去创建${franchiseeShortTitle}`}
                  onCreated={handleFranchiseeCreated}
                  onChange={handleFranchisee}
                  allowClear
                  extraParams={{
                    businessMode,
                  }}
                />}
                {businessMode === BusinessMode.Joint && <FormFranchisee
                  label={franchiseeTitle}
                  name='franchiseeName'
                  placeholder={`请输入${franchiseeTitle}`}
                  createBtnTitle={`去创建${franchiseeShortTitle}`}
                  onCreated={handleFranchiseeCreated}
                  onChange={handleFranchisee}
                  allowClear
                  extraParams={{
                    businessMode,
                  }}
                />}
              </Col>
              {standardFranchiseeId && <Col span={12}>
                <V2FormInput
                  label={uniqueName}
                  name='uniqueId'
                  disabled
                />
              </Col>}
            </>
            : ''}
          <Col span={12}>
            <V2FormSelect
              label='紧急度'
              name='emergencyDegree'
              options={emergencyDegree} />
          </Col>
          <Col span={12}>
            <V2FormDatePicker
              label='期望落位日期'
              name='expectDropInDate'
            />
          </Col>

        </Row>
        <V2Title type='H2' divider text={`选择${expansionTaskAlias || '拓店'}责任人`} style={{ margin: '16px 0' }} />
        <V2FormRadio
          name='radio'
          className={styles.radioList}
          options={[
            { label: '指派给自己', value: 1 },
            {
              label: isAssignTypeLeader ? (
                <div className={styles.radioSelect}>
                    指派给开发主管
                  <V2FormSelect
                    className={styles.assignAccountIds}
                    placeholder='请选择开发主管'
                    name='managerId'
                    options={refactorSelection(employeeOptions)}
                    config={{
                      showSearch: true,
                      onClick: e => {
                        e.preventDefault();
                        e.stopPropagation;
                      }, // 解决下拉框闪现的情况
                      onFocus: () => {
                        form.setFieldValue('radio', 2);
                      }, // 下拉框获取焦点的时候单选选择第二个
                    }}
                  />
                </div>
              ) : (
                <div className={styles.radioSelect}>
                    指派给团队成员
                  <V2FormSelect
                    className={styles.assignAccountIds}
                    placeholder='请选择指派人'
                    name='assignAccountIds'
                    options={refactorSelection(employeeOptions)}
                    config={{
                      showSearch: true,
                      onClick: e => {
                        e.preventDefault();
                        e.stopPropagation;
                      }, // 解决下拉框闪现的情况
                      onFocus: () => {
                        form.setFieldValue('radio', 2);
                      }, // 下拉框获取焦点的时候单选选择第二个
                    }}
                  />
                </div>
              ),
              value: 2,
            },
          ]}
        />
      </V2Form>
    </>
  );
};

export default EditMain;
